import { onDomReady } from 'utils/onDomReady'
import debounce from 'utils/debounce'
import { on } from 'delegated-events'
import { trackEvent } from 'api/index'

import { updateSelectedItemsOnPageLoad } from './multiselect'

const DASHBOARD_SEARCH_SELECTOR = '.dashboards-search__input'
const DASHBOARDS_SELECTOR = '.data-filter-section .dashboards__list'
const ITEM_HIDDEN_CLASSNAME = 'dashboard--hidden'
const DASHBOARD_MULTISELECT_CLEAR_SELECTOR =
  '.dashboards__filter .multiselect__clear'
const DASHBOARD_LOAD_MORE_SELECTOR = '.load-more-dashboards'
const DASHBOARD_ACTION_ANCHOR = '.dashboard-action-anchor'

const itemsPerPage = () => {
  if (window.matchMedia('(max-width: 650px)').matches) {
    return 10
  }
  if (window.matchMedia('(max-width: 1022px)').matches) {
    return 10
  }
  return 9
}

const sourceExpansionMap = {
  Xero: ['Xero Reports']
}

let visibleItemsCount = itemsPerPage()
let categoryValue = []
let categoryValueURL = []
let sourceValue = []
let destinationValue = []
let searchValue = ''
let fullItemList = []

const sectionElement = document.querySelector('.data-filter-section.filters')

function updateURL() {
  const urlParams = new URLSearchParams()

  if (categoryValueURL.length > 0) {
    urlParams.append('category', categoryValueURL[0])
  }

  if (sourceValue.length > 0) {
    sourceValue.forEach(source => {
      urlParams.append('source', source)
    })
  }

  if (destinationValue.length > 0) {
    destinationValue.forEach(destination => {
      urlParams.append('destination', destination)
    })
  }

  window.history.replaceState(null, '', `?${urlParams.toString()}`)
}

const scrollToStart = function () {
  const start = sectionElement.querySelector(
    '.data-filter-section--dashboards .anchor'
  )
  start.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const categoryHierarchyElement = sectionElement
const categoryHierarchyData = categoryHierarchyElement?.dataset.categories
  ? JSON.parse(categoryHierarchyElement.dataset.categories)
  : null

const categoryHierarchy = categoryHierarchyData
  ? Object.fromEntries(
      Object.entries(categoryHierarchyData).map(([key, value]) => [
        key,
        [key, ...(value.children || [])]
      ])
    )
  : {}

const handleCheckboxChange = function () {
  const value = this.value
  let array, eventName

  switch (this.name) {
    case 'categories-checkboxes':
      if (categoryValueURL.includes(value)) {
        categoryValue = []
        categoryValueURL = []
      } else {
        categoryValue = categoryHierarchy[value]
        categoryValueURL = [value]
      }

      eventName = 'Marketing:Templates:Category selected'

      trackEvent(eventName, {
        name: categoryValue[0] || 'None'
      })
      break
    case 'sources-checkboxes':
    case 'destinations-checkboxes':
      array =
        this.name === 'sources-checkboxes' ? sourceValue : destinationValue
      eventName =
        this.name === 'sources-checkboxes'
          ? 'Marketing:Templates:Source selected'
          : 'Marketing:Templates:Destination selected'

      if (this.checked) {
        array.push(value)
      } else {
        const index = array.indexOf(value)
        if (index !== -1) array.splice(index, 1)
      }

      trackEvent(eventName, {
        name: array.join(', ')
      })
      break
    default:
      return
  }

  updateURL()
  filterList()
  scrollToStart()
}

const sortDashboards = (dashboards, selectedCategory) => {
  const hasSorting = document
    .querySelector(DASHBOARDS_SELECTOR)
    ?.classList.contains('dashboards__list--sorted')

  if (hasSorting) {
    return dashboards
  }

  return dashboards.sort((a, b) => {
    const isAPrimary =
      a.dataset.primaryCategory?.trim().toLowerCase() ===
      selectedCategory?.trim().toLowerCase()
    const isBPrimary =
      b.dataset.primaryCategory?.trim().toLowerCase() ===
      selectedCategory?.trim().toLowerCase()

    if (isAPrimary !== isBPrimary) return isBPrimary - isAPrimary

    //show dashboards with 1-2 sources first
    if (sourceValue.length > 0) {
      const aSourcesLen = a.parsedData.sources.length
      const bSourcesLen = b.parsedData.sources.length

      const aPriority = aSourcesLen === 1 || aSourcesLen === 2 ? 1 : 0
      const bPriority = bSourcesLen === 1 || bSourcesLen === 2 ? 1 : 0

      if (aPriority !== bPriority) {
        return bPriority - aPriority
      }
    }

    return Number(b.dataset.position) - Number(a.dataset.position)
  })
}

const filterList = () => {
  const list = sectionElement.querySelector(DASHBOARDS_SELECTOR)
  if (!list) return

  const sortedItems = sortDashboards([...fullItemList], categoryValueURL[0])

  list.innerHTML = ''

  let showedMatchedItemsCount = 0
  let matchedItemsCount = 0

  const isItemMatched = item => {
    const {
      title,
      categories: itemCategories,
      sources: itemSources,
      destinations: itemDestinations
    } = item.parsedData

    const isSearchMatch = searchValue.length
      ? [title, ...itemSources, ...itemDestinations].some(it =>
          it.toLowerCase().trim().includes(searchValue)
        )
      : true

    const isCategoriesMatch = categoryValue.length
      ? categoryValue.some(item => itemCategories.includes(item))
      : true

    const isSourcesMatch = sourceValue.length
      ? itemSources.some(itemSource => {
          return sourceValue.some(source => {
            if (itemSource === source) return true

            const children = sourceExpansionMap[source] || []
            return children.includes(itemSource)
          })
        })
      : true

    const isDestinationsMatch = destinationValue.length
      ? destinationValue.some(item => itemDestinations.includes(item))
      : true

    return (
      isSearchMatch &&
      isCategoriesMatch &&
      isSourcesMatch &&
      isDestinationsMatch
    )
  }

  sortedItems.forEach(item => {
    if (isItemMatched(item)) {
      if (showedMatchedItemsCount < visibleItemsCount) {
        item.classList.remove(ITEM_HIDDEN_CLASSNAME)
        showedMatchedItemsCount++
      } else {
        item.classList.add(ITEM_HIDDEN_CLASSNAME)
      }
      list.appendChild(item)
      matchedItemsCount++
    } else {
      item.classList.add(ITEM_HIDDEN_CLASSNAME)
    }
  })

  const isEmpty = sortedItems.every(item =>
    item.classList.contains(ITEM_HIDDEN_CLASSNAME)
  )

  list.classList.toggle('is-empty', isEmpty)

  const remainingItems = matchedItemsCount - visibleItemsCount

  sectionElement.querySelector(DASHBOARD_LOAD_MORE_SELECTOR).style.display =
    remainingItems > 0 ? 'inline-flex' : 'none'
}

on('submit', '.dashboards__filter', e => {
  e.preventDefault()
})

on(
  'input',
  DASHBOARD_SEARCH_SELECTOR,
  debounce(e => {
    searchValue = e.target.value?.toLowerCase()?.trim() ?? ''
    filterList()
    trackEvent('Marketing:Templates:Search', { name: searchValue })
  })
)

on(
  'change',
  `[name="sources-checkboxes"], [name="categories-checkboxes"], [name="destinations-checkboxes"]`,
  handleCheckboxChange
)

const syncFiltersFromURL = function () {
  const urlParams = new URLSearchParams(window.location.search)

  categoryValueURL = urlParams.getAll('category')
  categoryValue = categoryValueURL.flatMap(value => categoryHierarchy[value])

  sourceValue = urlParams.getAll('source')
  destinationValue = urlParams.getAll('destination')
  searchValue = ''

  const categoryCheckboxes = sectionElement.querySelectorAll(
    `[name="categories-checkboxes"]`
  )

  const sourceCheckboxes = sectionElement.querySelectorAll(
    `[name="sources-checkboxes"]`
  )

  const destinationCheckboxes = sectionElement.querySelectorAll(
    `[name="destinations-checkboxes"]`
  )

  categoryCheckboxes.forEach(checkbox => {
    checkbox.checked = categoryValueURL.includes(checkbox.value)
  })

  sourceCheckboxes.forEach(checkbox => {
    checkbox.checked = sourceValue.includes(checkbox.value)
  })

  destinationCheckboxes.forEach(checkbox => {
    checkbox.checked = destinationValue.includes(checkbox.value)
  })

  updateSelectedItemsOnPageLoad()
}

onDomReady(() => {
  syncFiltersFromURL()

  fullItemList = Array.from(sectionElement.querySelectorAll('.dashboard')).map(
    item => {
      item.parsedData = {
        title: item.dataset.title,
        text: item.dataset.text,
        categories: JSON.parse(item.dataset.categories),
        sources: JSON.parse(item.dataset.sources),
        destinations: JSON.parse(item.dataset.destinations)
      }
      return item
    }
  )

  filterList()
})

on('click', DASHBOARD_LOAD_MORE_SELECTOR, () => {
  visibleItemsCount += itemsPerPage()
  filterList()
})

on('click', DASHBOARD_MULTISELECT_CLEAR_SELECTOR, event => {
  const { collection } = event.target.dataset
  switch (collection) {
    case 'categories': {
      categoryValue = []
      categoryValueURL = []
      break
    }
    case 'sources': {
      sourceValue = []
      break
    }
    case 'destinations': {
      destinationValue = []
      break
    }
  }
  updateURL()
  filterList()
})

on('click', DASHBOARD_ACTION_ANCHOR, e => {
  e.preventDefault()

  const href = e.target.closest(DASHBOARD_ACTION_ANCHOR).getAttribute('href')

  const [baseUrl, anchor] = href.split('#')

  if (baseUrl) {
    const newParams = new URLSearchParams(baseUrl.split('?')[1] || '')

    const newUrl = `${window.location.pathname}?${newParams.toString()}${
      anchor ? `#${anchor}` : ''
    }`

    window.history.pushState(null, '', newUrl)

    syncFiltersFromURL()
    filterList()
  }

  if (anchor) {
    const targetElement = sectionElement.querySelector(`#${anchor}`)
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
})
